
import React, { useState, useEffect, useMemo } from 'react';
import { dbService } from '../services/dbService.ts';
import { aiService } from '../services/aiService.ts';
import { Job, SubscriptionTier } from '../types.ts';

const JobsView = ({ user, onLogout, onChangeTab, activeTab = 'jobs' }: any) => {
  const [profile] = useState(dbService.getStudentProfile(user.id));
  const [jobs, setJobs] = useState<Job[]>(dbService.getJobs());
  const [isLoading, setIsLoading] = useState(false);
  const [activeSegment, setActiveSegment] = useState<'recommended' | 'local' | 'industry'>('recommended');
  
  const userTier = user.tier || SubscriptionTier.BASIC;
  const isLocked = userTier === SubscriptionTier.BASIC;

  useEffect(() => {
    if (!isLocked && jobs.length === 0) fetchJobs();
  }, [profile?.major]);

  const fetchJobs = async () => {
    setIsLoading(true);
    try {
      const results = await aiService.getJobsAndCoops(profile);
      const stabilized = results.map((j, i) => ({ ...j, id: j.id || `job-${i}` }));
      setJobs(stabilized);
      dbService.saveJobs(stabilized);
    } catch (e) { console.error(e); }
    finally { setIsLoading(false); }
  };

  const filteredJobs = useMemo(() => {
    let result = [...jobs];
    if (activeSegment === 'recommended') {
        return result.sort((a, b) => (b.matchScore || 0) - (a.matchScore || 0));
    } else if (activeSegment === 'local') {
        return result.filter(j => j.location.toLowerCase().includes(profile?.city?.toLowerCase() || ''));
    } else if (activeSegment === 'industry') {
        return result.sort((a, b) => a.industry.localeCompare(b.industry));
    }
    return result;
  }, [jobs, activeSegment, profile]);

  return (
    <div className="p-12 lg:p-20 bg-slate-50/30 dark:bg-slate-950/30 min-h-screen transition-colors duration-500 overflow-y-auto">
        <header className="mb-12 flex flex-col md:flex-row justify-between items-end gap-6">
          <div>
             <span className="text-[10px] font-black uppercase tracking-[0.4em] text-indigo-600 dark:text-indigo-400 mb-2 block">Industry Radar</span>
             <h1 className="text-5xl font-black text-blue-950 dark:text-white tracking-tighter">Market Pulse.</h1>
          </div>
          {!isLocked && (
            <div className="bg-white dark:bg-slate-900 p-1.5 rounded-[2.2rem] shadow-xl border border-slate-100 dark:border-slate-800 flex gap-2 overflow-x-auto scrollbar-hide">
               <button 
                 onClick={() => setActiveSegment('recommended')}
                 className={`px-8 py-3.5 rounded-[1.8rem] text-[10px] font-black uppercase tracking-widest transition-all ${activeSegment === 'recommended' ? 'bg-indigo-600 text-white shadow-xl shadow-indigo-600/20' : 'text-slate-400 hover:text-blue-950 dark:hover:text-white'}`}
               >
                 Sage Top Picks
               </button>
               <button 
                 onClick={() => setActiveSegment('local')}
                 className={`px-8 py-3.5 rounded-[1.8rem] text-[10px] font-black uppercase tracking-widest transition-all ${activeSegment === 'local' ? 'bg-indigo-600 text-white shadow-xl shadow-indigo-600/20' : 'text-slate-400 hover:text-blue-950 dark:hover:text-white'}`}
               >
                 Local Hub
               </button>
               <button 
                 onClick={() => setActiveSegment('industry')}
                 className={`px-8 py-3.5 rounded-[1.8rem] text-[10px] font-black uppercase tracking-widest transition-all ${activeSegment === 'industry' ? 'bg-indigo-600 text-white shadow-xl shadow-indigo-600/20' : 'text-slate-400 hover:text-blue-950 dark:hover:text-white'}`}
               >
                 By Industry
               </button>
            </div>
          )}
        </header>

        {isLocked ? (
          <div className="bg-white dark:bg-slate-900 p-20 rounded-[4rem] border border-slate-100 dark:border-slate-800 shadow-4xl text-center flex flex-col items-center animate-in zoom-in-95 duration-500">
             <div className="w-24 h-24 bg-blue-50 dark:bg-indigo-900/20 text-blue-600 dark:text-indigo-400 rounded-[2.5rem] flex items-center justify-center text-4xl mb-10 shadow-inner">
                <i className="fas fa-lock"></i>
             </div>
             <h2 className="text-4xl font-black text-blue-950 dark:text-white tracking-tighter mb-4">Career Radar Locked.</h2>
             <p className="text-slate-500 dark:text-slate-400 text-lg font-medium max-w-md mb-12 leading-relaxed">
               Access real-time job/co-op listings contextualized for your career trajectory. Upgrade to <span className="text-blue-600 dark:text-indigo-400 font-black">Standard</span> to unlock.
             </p>
             <button 
               onClick={() => onChangeTab('membership')}
               className="px-12 py-5 bg-blue-900 dark:bg-indigo-600 text-white rounded-3xl font-black text-lg hover:bg-blue-800 transition shadow-2xl shadow-blue-900/20 hover:-translate-y-1 active:scale-95"
             >
                Upgrade to Standard
             </button>
          </div>
        ) : isLoading ? (
          <div className="h-96 flex flex-col items-center justify-center bg-white dark:bg-slate-900 rounded-[4rem] border border-slate-100 dark:border-slate-800 shadow-2xl">
             <div className="animate-spin rounded-full h-16 w-16 border-b-2 border-indigo-600 mb-6"></div>
             <p className="text-[10px] font-black uppercase text-indigo-400 tracking-[0.4em] animate-pulse">Running Market Simulation...</p>
          </div>
        ) : (
          <div className="space-y-8 pb-32">
             {filteredJobs.length === 0 ? (
                <div className="p-16 text-center text-slate-400 dark:text-slate-600 bg-white dark:bg-slate-900 rounded-[3rem] border border-dashed border-slate-200 dark:border-slate-800">
                   <p className="font-bold text-sm uppercase tracking-widest">No matching career nodes found.</p>
                </div>
             ) : filteredJobs.map(job => (
               <div key={job.id} className="bg-white dark:bg-slate-900 rounded-[3.5rem] border border-slate-100 dark:border-slate-800 shadow-xl overflow-hidden hover:border-indigo-500/30 hover:shadow-2xl transition-all duration-300 group relative">
                  {job.matchScore && job.matchScore > 85 && (
                    <div className="absolute top-0 right-0 bg-indigo-600 text-white px-8 py-5 rounded-bl-[3rem] text-[10px] font-black uppercase tracking-[0.2em] z-10 shadow-lg border-b border-l border-indigo-400/30">
                       Sage Match {job.matchScore}%
                    </div>
                  )}

                  <div className="p-12 flex flex-col lg:flex-row gap-12 items-start">
                     <div className="w-24 h-24 bg-slate-50 dark:bg-slate-800 rounded-3xl flex items-center justify-center text-4xl text-slate-300 dark:text-slate-600 group-hover:bg-indigo-50 dark:group-hover:bg-indigo-900/30 group-hover:text-indigo-600 transition-colors shrink-0 shadow-inner">
                        <i className="fas fa-building"></i>
                     </div>

                     <div className="flex-1">
                        <div className="flex flex-wrap gap-3 mb-6">
                           <span className="px-4 py-1.5 bg-slate-50 dark:bg-slate-800 text-slate-500 dark:text-slate-400 rounded-xl text-[9px] font-black uppercase tracking-widest border border-slate-100 dark:border-slate-700">{job.type}</span>
                           <span className="px-4 py-1.5 bg-slate-50 dark:bg-slate-800 text-slate-500 dark:text-slate-400 rounded-xl text-[9px] font-black uppercase tracking-widest border border-slate-100 dark:border-slate-700">{job.location}</span>
                           <span className="px-4 py-1.5 bg-indigo-50 dark:bg-indigo-900/30 text-indigo-600 dark:text-indigo-300 rounded-xl text-[9px] font-black uppercase tracking-widest border border-indigo-100 dark:border-indigo-900/50">{job.industry}</span>
                        </div>
                        <h3 className="text-3xl font-black text-blue-950 dark:text-white leading-tight mb-2 group-hover:text-indigo-600 transition-colors tracking-tight">{job.title}</h3>
                        <p className="text-xl font-bold text-slate-400 dark:text-slate-500 mb-8">{job.company}</p>
                        <p className="text-base text-slate-600 dark:text-slate-400 font-medium leading-relaxed max-w-3xl mb-10">{job.description}</p>
                        
                        <a href={job.sourceUrl} target="_blank" rel="noreferrer" className="inline-flex items-center gap-4 px-10 py-5 bg-blue-950 dark:bg-indigo-600 text-white rounded-[1.8rem] font-black text-xs uppercase tracking-widest hover:bg-blue-800 dark:hover:bg-indigo-500 transition shadow-4xl hover:-translate-y-1 active:scale-95">
                           Initiate Application <i className="fas fa-external-link-alt text-[10px]"></i>
                        </a>
                     </div>
                  </div>
               </div>
             ))}
          </div>
        )}
    </div>
  );
};

export default JobsView;
